import { createSignal } from "solid-js"

export type DialogStackItem = {
  id: string
  closing: () => boolean
  setClosing: (closing: boolean) => void
  dispose: () => void
  onClose?: () => void
}

export function createDialogStack<T extends DialogStackItem>() {
  const [stack, setStack] = createSignal<T[]>([])
  const timers = new Map<T, ReturnType<typeof setTimeout>>()
  const active = () => stack().findLast((item) => !item.closing())

  const remove = (matches: (item: T) => boolean) => {
    const removed = stack().filter(matches)
    if (removed.length === 0) return
    setStack((items) => items.filter((item) => !removed.includes(item)))
    for (const item of removed) {
      clearTimeout(timers.get(item))
      timers.delete(item)
      item.dispose()
    }
  }

  return {
    stack,
    active,
    add(item: T) {
      setStack((items) => [...items, item])
    },
    close(id?: string) {
      const current = id ? stack().find((item) => item.id === id) : active()
      if (!current || current.closing()) return

      // Exiting layers must release modal ownership before focus returns to the parent.
      current.setClosing(true)
      timers.set(
        current,
        setTimeout(() => remove((item) => item === current), 100),
      )
      current.onClose?.()
    },
    clearClosing() {
      remove((item) => item.closing())
    },
    clear() {
      remove(() => true)
    },
  }
}
